import React from 'react';
import { useHistory } from 'react-router-dom';
import qs from 'qs';

type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;
type Subtract<T extends K, K> = Omit<T, keyof K>;

export interface WithBackUrlProps {
  backUrl: string;
  isBackUrlRelative: boolean;
  isGoBackHistory?: boolean;
}

function getCurrentAppHost(removeLastSlash = false) {
  const match = window.location.href.match(/([^/]*\/){4}/);
  if (!match) {
    return null;
  }

  return removeLastSlash ? match[0].slice(0, -1) : match[0];
}

const isRelativeLink = (url: string) => {
  const startsWithHTTP = /^https?:\/\//i.test(decodeURI(url));
  const subPath = url.match(/([^/]*\/){4}/);

  if (!startsWithHTTP) {
    return true;
  }

  if (!subPath) {
    return false;
  }

  const [appHost] = subPath;
  const currentAppHost = getCurrentAppHost();

  return appHost === currentAppHost;
};

function withBackUrl<
  C extends React.ComponentType<React.ComponentProps<C> & WithBackUrlProps>,
  ResolvedProps = JSX.LibraryManagedAttributes<
    C,
    Subtract<React.ComponentProps<C>, WithBackUrlProps>
  >
>(WrappedComponent: C) {
  type PrivateProps = {
    defaultBack: string;
  };

  type Props = ResolvedProps & PrivateProps;

  function BackUrl({ defaultBack, ...props }: Props) {
    const history = useHistory();

    const getBackUrl = () => {
      try {
        const queryString = qs.parse(history.location.search, {
          ignoreQueryPrefix: true,
        });
        const backUrl: any = queryString.from ?? defaultBack;
        const isBackUrlRelative = isRelativeLink(backUrl);
        const appHost = getCurrentAppHost(true);

        if (isBackUrlRelative && appHost) {
          return {
            backUrl: backUrl.replace(appHost, ''),
            isBackUrlRelative,
          };
        }

        return {
          backUrl,
          isBackUrlRelative,
        };
      } catch (e) {
        return {
          backUrl: '/',
          isBackUrlRelative: true,
        };
      }
    };

    const { backUrl, isBackUrlRelative } = getBackUrl();
    const rest = props as JSX.LibraryManagedAttributes<
      C,
      React.ComponentProps<C>
    >;

    return (
      <WrappedComponent
        backUrl={backUrl}
        isBackUrlRelative={isBackUrlRelative}
        {...rest}
      />
    );
  }

  BackUrl.defaultProps = {
    defaultBack: '/',
  };

  return BackUrl;
}

export default withBackUrl;
